<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                <v-icon class="me-2">mdi-calendar-check</v-icon>
                Attendance Raw Punch Data Report
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="fetchSheet">
                  <v-row class="py-3 px-3" align="end" dense>
                    <v-col cols="12" md="3">
                      <v-date-input
                          input-format="dd/mm/yyyy"
                          v-model="filters.from_date"
                          label="From Date"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          hide-details="auto"
                          clearable
                          :max="filters.to_date"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-date-input
                          input-format="dd/mm/yyyy"
                          v-model="filters.to_date"
                          label="To Date"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          hide-details="auto"
                          clearable
                          :min="filters.from_date"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-autocomplete
                          v-model="filters.employee_id"
                          :items="employees"
                          :item-title="formatEmployeeLabel"
                          item-value="id"
                          label="Employee (Optional)"
                          variant="outlined"
                          density="compact"
                          bg-color="grey-lighten-4"
                          :loading="employeesLoading"
                          clearable
                          hide-details="auto"
                          placeholder="Search employee"
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="d-flex align-end">
                      <v-btn
                          type="submit"
                          color="primary"
                          variant="flat"
                          :loading="loading"
                          prepend-icon="mdi-magnify"
                          density="compact"
                          height="40"
                      >
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>

        <v-col cols="12">
          <v-expansion-panels v-model="reportPanel" class="mt-3">
            <v-expansion-panel>
              <v-expansion-panel-title
                  color="blue-grey-lighten-4"
                  class="panel-header"
                  expand-icon="mdi-chevron-down"
              >
                <v-icon class="me-2">mdi-table</v-icon>
                Attendance Raw Punch Data
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                        v-model="search"
                        append-inner-icon="mdi-magnify"
                        label="Search"
                        single-line
                        hide-details
                        density="compact"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        class="pl-2"
                        clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                          v-if="sheetData.length > 0"
                          variant="outlined"
                          density="compact"
                          size="x-small"
                          divided
                      >
                        <v-btn
                            prepend-icon="mdi mdi-download"
                            variant="flat"
                            color="success"
                            @click="exportToExcel"
                            :disabled="!sheetData.length"
                        >EXCEL</v-btn
                        >
                        <v-btn
                            prepend-icon="mdi mdi-download"
                            variant="flat"
                            color="red"
                            @click="exportToPDF"
                            :disabled="!sheetData.length"
                        >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12"></v-col>
                </v-row>

                <v-progress-linear
                    v-if="loading"
                    indeterminate
                    color="primary"
                    class="mb-4"
                />

                <v-alert
                    v-if="!loading && filteredSheetData.length === 0"
                    type="info"
                    variant="tonal"
                >
                  No attendance records found for the selected criteria.
                </v-alert>

                <div v-if="filteredSheetData.length">
                  <v-table density="comfortable" class="text-caption table-bordered">
                    <thead>
                    <tr>
                      <th># SL</th>
                      <th>Employee Code</th>
                      <th>Employee Name</th>
                       <th>Designation</th>
                      <th>Department</th>
                     
                      
                      <th>Date</th>
                      <th>Punch Time</th>
                      <th>Entry Source</th>
                    </tr>
                    </thead>
                    <tbody >
                    <tr v-for="(row, index) in filteredSheetData" :key="`${row.employee?.code || row.id}-${row.punch_time}-${index}`">
                      <td class="text-center">{{ index + 1 }}</td>
                      <td>{{ row.employee?.code || '-' }}</td>
                      <td>{{ row.employee?.name || '-' }}</td>
                          <td>{{ row.employee?.designation?.name || '-' }}</td>
                      <td>{{ row.employee?.department?.name || '-' }}</td>
                  
                     
                      <td class="text-center">{{ formatDateView(row.punch_time) }}</td>
                      <td class="text-center">{{ formatTime(extractTimeFromDatetime(row.punch_time)) }}</td>
                      <td>{{ row.entry_source || '-' }}</td>
                    </tr>
                    </tbody>
                  </v-table>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, reactive, computed, onMounted, getCurrentInstance } from 'vue'
import { toast } from 'vue3-toastify'
import { useStore } from 'vuex'
import { formatDateView, formatDateDash } from "@/plugins/helpers";


const store = useStore()
const instance = getCurrentInstance()
const appContext = instance?.appContext

const employees = ref([])
const employeesLoading = ref(false)
const loading = ref(false)
const sheetData = ref([])
const panel = ref([0])
const reportPanel = ref([0])
const search = ref('')

const filters = reactive({
  from_date: '',
  to_date: '',
  employee_id: null
})

const formatEmployeeLabel = item => {
  if (!item) return ''
  return `${item.name || ''}${item.employee_code ? ` - ${item.employee_code}` : ''}`
}



const formatNumber = (value, fraction = 0) => {
  if (value === undefined || value === null || isNaN(value)) return '-'
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction
  })
}

const extractTimeFromDatetime = (val) => {
  if (!val) return ''
  const str = typeof val === 'string' ? val : val
  const spaceIdx = str.indexOf(' ')
  return spaceIdx >= 0 ? str.slice(spaceIdx + 1) : str
}

const formatTime = (timeStr) => {
  if (!timeStr || timeStr === '-') return '-'

  // Handle time strings like "08:30:00" or "08:30"
  const timeParts = String(timeStr).split(':')
  if (timeParts.length < 2) return timeStr

  const hours = parseInt(timeParts[0], 10)
  const minutes = timeParts[1]

  if (isNaN(hours)) return timeStr

  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours

  return `${displayHours}:${minutes} ${period}`
}

// Filtered sheet data based on search
const filteredSheetData = computed(() => {
  if (!search.value) {
    return sheetData.value
  }

  const searchLower = search.value.toLowerCase()
  return sheetData.value.filter(row => {
    const employeeCode = (row.employee?.code || '').toLowerCase()
    const employeeName = (row.employee?.name || '').toLowerCase()
    const department = (row.employee?.department?.name || '').toLowerCase()
    const designation = (row.employee?.designation?.name || '').toLowerCase()
    return employeeCode.includes(searchLower) ||
        employeeName.includes(searchLower) ||
        department.includes(searchLower) ||
        designation.includes(searchLower)
  })
})


const setDefaultDates = () => {
  const today = new Date()
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  filters.from_date = startOfMonth
  filters.to_date = today
}

const loadEmployees = async () => {
  employeesLoading.value = true
  try {
    const response = await store.dispatch('getEmployees')
    employees.value = response.data.data || []
  } catch (error) {
    employees.value = []
    toast.error(error.message || 'Failed to load employees')
  } finally {
    employeesLoading.value = false
  }
}

const fetchSheet = async () => {
  if (!filters.from_date || !filters.to_date) {
    toast.warning('Please select both From Date and To Date')
    return
  }

  loading.value = true
  try {
    const params = {
      from_date: formatDateDash(filters.from_date),
      to_date: formatDateDash(filters.to_date),
      employee_id: filters.employee_id || undefined
    }

    const response = await store.dispatch('getAttendanceRawPunchDataReport', params)
    if (response.success) {
      sheetData.value = response.data || []
    } else {
      sheetData.value = []
      toast.error(response.message || 'Failed to fetch attendance raw punch data')
    }
  } catch (error) {
    sheetData.value = []
    toast.error(error.response?.data?.message || error.message || 'Failed to fetch attendance raw punch data')
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  setDefaultDates()
  filters.employee_id = null
  sheetData.value = []
}

// Computed for company info
const getCurrentCompany = computed(() => {
  return store.getters.getCurrentCompany || {}
})

const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl || ''
})

// Export to Excel
const exportToExcel = () => {
  if (!sheetData.value.length) {
    toast.warning('No data to export')
    return
  }

  const data = [
    ['Attendance Raw Punch Data'],
    [`From Date: ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}`]
  ]

  if (filters.employee_id) {
    const selectedEmployee = employees.value.find(e => e.id === filters.employee_id)
    if (selectedEmployee) {
      data.push([`Employee: ${selectedEmployee.name || ''}${selectedEmployee.employee_code ? ` (${selectedEmployee.employee_code})` : ''}`])
    }
  }

  data.push([], ['# SL', 'Date', 'Employee Code', 'Employee Name', 'Designation', 'Department', 'Punch Type', 'Punch Time', 'Entry Source'], [])

  // Add data rows - backend returns raw punch records with employee relation
  sheetData.value.forEach((row, index) => {
    data.push([
      index + 1,
      formatDateView(row.punch_time),
      row.employee?.code || '-',
      row.employee?.name || '-',
         row.employee?.designation?.name || '-',
      row.employee?.department?.name || '-',
   
      
      formatTime(extractTimeFromDatetime(row.punch_time)) || '-',
      row.entry_source || '-'
    ])
  })

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'attendance_raw_punch_data' + proxy.$fileDate())
  }
}

// Export to PDF
const exportToPDF = async () => {
  if (!sheetData.value.length) {
    toast.warning('No data to export')
    return
  }

  const company = getCurrentCompany.value
  const fileServerUrl = getFileServerUrl.value
  const selectedEmployee = filters.employee_id
      ? employees.value.find(e => e.id === filters.employee_id)
      : null

  let printWindow = window.open('', 'PRINT', `width=${screen.width}, height=${screen.height}, left=0, top=0`)
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <meta http-equiv="X-UA-Compatible" content="ie=edge">
        <title>Attendance Raw Punch Data</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Arimo:ital,wght@0,400..700;1,400..700&display=swap" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.8/dist/css/bootstrap.min.css" rel="stylesheet">
        <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
        <style>
          body { font-family: 'Arimo', sans-serif; margin: 0px; }
          .invoice-header { display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; }
          .invoice-header img { max-height: 70px; max-width: 150px; }
          .table th, .table td { border: 1px solid #000; padding: 8px; vertical-align: middle; font-size: 12px; }
          .table th { background-color: #f0f0f0; color: #333; }
          .text-end { text-align: right; }
          .text-center { text-align: center; }
          h4 { font-size: 20px; color: #333; }
          p { font-size: 14px; color: #555; margin: 2px 0; }
          strong { font-weight: bold; color: #333; }
          @page { margin: 0.5in; size: letter; }
          .logo-container { display:flex; align-items:center; }
          .logo-container img { margin-left: 10px; }
          .table { width: 100%; border-collapse: collapse; table-layout: auto; }
          thead { display: table-header-group; }
          tfoot { display: none; }
          .totals-row { page-break-inside: avoid; }
          .totals-row td { font-weight: bold; background-color: #f5f5f5 !important; }
        </style>
      </head>
      <body>
        <div class="container-fluid position-relative">
          <div class="invoice-header">
            <div>
              <h4 class="text-uppercase">${company?.name || 'Company Name'}</h4>
              <p style="width:65%">${company?.address || ''}</p>
              <p><i class="mdi mdi-phone-outline"></i> ${company?.mobile || ''}</p>
              <p><i class="mdi mdi-email-outline"></i> ${company?.email || ''}</p>
              ${company?.website ? `<p><i class="mdi mdi-web"></i> ${company.website}</p>` : ''}
            </div>
            <div class="logo-container">
              ${company?.logo_thumb ? `<img src="${fileServerUrl}${company.logo_thumb}" alt="Logo" class="img-fluid">` : ''}
            </div>
          </div>
          <h4 class="text-center my-4"><strong>ATTENDANCE RAW PUNCH DATA</strong></h4>
          <div class="row mb-3">
            <div class="col-sm-8">
              <p><strong>Period:</strong> ${formatDateView(filters.from_date)} to ${formatDateView(filters.to_date)}</p>
              ${selectedEmployee ? `<p><strong>Employee:</strong> ${selectedEmployee.name || ''} ${selectedEmployee.employee_code ? `(${selectedEmployee.employee_code})` : ''}</p>` : ''}
            </div>
            <div class="col-sm-4 text-end">
              <p><strong>Total Records:</strong> ${sheetData.value.length}</p>
            </div>
          </div>
          <table class="table mt-4">
            <thead>
              <tr>
                <th>SN</th>
                
                <th>Employee Code</th>
                <th>Employee Name</th>
                 <th>Designation</th>
                <th>Department</th>
               <th>Date</th>
                
                <th>Punch Time</th>
                <th>Entry Source</th>
              </tr>
            </thead>
            <tbody>
              ${sheetData.value.map((row, index) => `
                <tr>
                  <td>${index + 1}</td>
                 
                  <td>${row.employee?.code || '-'}</td>
                  <td><strong>${row.employee?.name || '-'}</strong></td>
                      <td>${row.employee?.designation?.name || '-'}</td>
                  <td>${row.employee?.department?.name || '-'}</td>
              
                   <td>${formatDateView(row.punch_time)}</td>
                  <td>${formatTime(extractTimeFromDatetime(row.punch_time)) || '-'}</td>
                  <td>${row.entry_source || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </body>
    </html>
  `)
  printWindow.moveTo(0, 0)
  printWindow.focus()
  await new Promise(resolve => setTimeout(resolve, 500))
  printWindow.print()
  printWindow.close()
}

onMounted(async () => {
  setDefaultDates()
  await loadEmployees()
})
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.table-bordered :deep(th),
.table-bordered :deep(td) {
  border: 1px solid #000000 !important;
}

.table-bordered :deep(table) {
  border-collapse: collapse;
}

.panel-header {
  font-weight: 600;
}
</style>

